import React, {FormEvent, useMemo, useState} from 'react';
import {connect} from 'react-redux';
import {Form, Formik, FormikErrors} from 'formik';
import { Alert, Grid, TextInput, Select } from '@mantine/core';
import {UserModalSchema} from './UserModalSchema';
import {User, userStore} from '../../../../../../redux/entities/user';
// removed legacy dropdown input; switching to Mantine layout/inputs
import {Role, roleStore} from '../../../../../../redux/entities/role';
import {makeUserRegistration} from '../../../../../../redux/factory/user';
import {AxiosError} from 'axios';
import {getErrorResponseMessage} from '../../../../../../util/http';
import {bindActionCreators, Dispatch} from 'redux';
// removed bootstrap/datepicker/phone/profile picture custom inputs for Mantine
import {CommonState} from '../../../../../../redux';
import {convertToDropDownOptions} from '../../../../../../util/form';
import {SubmitProps} from '../../../../../../types';
import StandardModal from '../../../../../../components/util/StandardModal/StandardModal';

export interface UserUpsert {
  id?: string;
  email: string;
  name: string;
  primaryPhone: string;
  secondaryPhone: string;
  address: string;
  addressLineTwo: string;
  city: string;
  state: string;
  zip: string;
  roleId: string;
}

type Props = SubmitProps & {
  editable: boolean;
  onTop?: boolean;
  existingUser?: User;
  role?: Role;
} & ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

function UserModal(props: Props) {
  const {onTop, onSubmit, editable, onCancel, existingUser, role, rolesOptions, actions: {upsertUser, updateProfilePictureAction}} = props;
  const getFieldName = (name: keyof User) => name;
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const processSubmit = async (
    e: FormEvent<HTMLFormElement>,
    association: UserUpsert,
    validate: (values: UserUpsert) => Promise<FormikErrors<UserUpsert>>,
    formikHandleSubmit: (e?: FormEvent<HTMLFormElement> | undefined) => void) => {
    setIsSubmitting(true);
    setErrorMessage('');
    e.persist();
    e.preventDefault();
    const errors = await validate(association);
    if (Object.values(errors).length !== 0) {
      formikHandleSubmit(e);
      setIsSubmitting(false);
    } else {
      try {
        await upsertUser({...association, roleId: association.roleId || role?.id || ''});
        onSubmit();
      } catch (e: AxiosError | any) {
        setErrorMessage(getErrorResponseMessage(e));
      }
    }
    setIsSubmitting(false);
  };

  // Profile picture update has been removed from this modal layout for simplicity

  // Buttons provided by StandardModal footer


  const title = existingUser?.id ? 'Update User' : `Add ${role?.roleName ? `${role.roleName} ` : ''}User`;
  const formId = 'user-modal-form';

  const initialValues = useMemo(() => {
    if (existingUser) return existingUser as UserUpsert;
    const newUser = makeUserRegistration();
    if (role) {
      newUser.roleId = role.id;
    }
    return newUser;
  }, [existingUser, role]);

  return (
    <StandardModal
      opened={true}
      onCancel={onCancel}
      title={title}
      size={'sm'}
      submitText={editable ? 'Submit' : 'Close'}
      cancelText={editable ? 'Cancel' : 'Close'}
      submitDisabled={isSubmitting || !editable}
      loading={isSubmitting}
      formId={editable ? formId : undefined}
      onSubmit={!editable ? onCancel : undefined}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={UserModalSchema}
        onSubmit={() => undefined}
      >
        {({ values, errors, touched, handleChange, handleBlur, validateForm, handleSubmit, setFieldValue }) => {
          return (
            <Form id={formId} noValidate={false} onSubmit={(e) => processSubmit(e, values, validateForm, handleSubmit)}>
              <Grid gutter="md" style={{ marginTop: '0.5rem', marginBottom: '1rem' }}>
                <Grid.Col span={12}>
                  <TextInput
                    label="Email"
                    name={getFieldName('email')}
                    value={(values as any).email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched?.email && (errors?.email as any)}
                    placeholder="name@example.org"
                    disabled={!editable}
                  />
                </Grid.Col>
                <Grid.Col span={12}>
                  <TextInput
                    label="Name"
                    withAsterisk
                    name={getFieldName('name')}
                    value={(values as any).name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched?.name && (errors?.name as any)}
                    placeholder="Full name"
                    disabled={!editable}
                  />
                </Grid.Col>
                <Grid.Col span={12}>
                  <Select
                    label="Role"
                    withAsterisk
                    data={rolesOptions as any}
                    value={(values as any).roleId || ''}
                    placeholder="Select a role"
                    onChange={(val) => setFieldValue('roleId', val || '')}
                    searchable
                    disabled={!editable || (existingUser?.role?.roleName === 'Administrator') || (!!role && !existingUser)}
                    error={touched?.roleId && (errors?.roleId as any)}
                  />
                </Grid.Col>
              </Grid>

              {errorMessage !== '' ? (
                <Alert color="red" variant="light" style={{ marginTop: '1rem' }}>
                  {errorMessage}
                </Alert>
              ) : null}
            </Form>
          );
        }}
      </Formik>
    </StandardModal>
  );
}

const mapDispatchToProps = (dispatch: Dispatch) => ({actions: bindActionCreators({
    upsertUser: userStore.actions.upsert,
    updateProfilePictureAction: userStore.actions.updateUsersProfilePicture
  }, dispatch)});
const mapStateToProps = (state: CommonState) => ({
  getRoleByName: roleStore.selectors.getRoleByName(state),
  rolesOptions: convertToDropDownOptions(roleStore.selectors.getAsArray(state), 'roleName')
});
export default connect(mapStateToProps, mapDispatchToProps)(UserModal);
